/**
 * Category browse — filtered listings for one category slug (and
 * optionally a country + intent).
 *
 * URL: /local/browse/:categorySlug?country=US&type=free
 *   • Uses `country` from query string; falls back to US
 *   • `type` narrows listing_type (optional)
 *
 * Deliberately minimal — just header + filter chips + grid + empty state.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Plus, Filter } from "lucide-react";
import { localApi, LISTING_TYPES, typeByKey, formatMoney } from "@/lib/localApi";


function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 3600) return `${Math.round(s / 60) || 1}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}


export default function LocalCategoryBrowse() {
  const { categorySlug } = useParams();
  const [params, setParams] = useSearchParams();
  const country = params.get("country") || "US";
  const type = params.get("type") || "";

  const [category, setCategory] = useState(null);
  const [countries, setCountries] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentCountry = useMemo(
    () => countries.find((c) => c.code === country) || null,
    [countries, country],
  );

  useEffect(() => {
    (async () => {
      const [countriesRes, catRes] = await Promise.all([
        localApi.get("/countries?active_only=true"),
        localApi.get("/categories"),
      ]);
      setCountries(countriesRes.data.items || []);
      const all = (catRes.data.items || []);
      // Fetch children so we know if this is a top-level with subs.
      const found = all.find((c) => c.slug === categorySlug);
      if (found) {
        setCategory(found);
      } else {
        // Not top-level — try fetching directly.
        try {
          const sub = await localApi.get(`/categories?parent=${categorySlug}`);
          if (sub.data.items?.length) setCategory({ slug: categorySlug, name: categorySlug });
        } catch {
          setCategory({ slug: categorySlug, name: categorySlug });
        }
      }
    })();
  }, [categorySlug]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({
      country_code: country,
      category: categorySlug,
      limit: "24",
    });
    if (type) qs.set("listing_type", type);
    localApi.get(`/listings?${qs.toString()}`)
      .then(({ data }) => setListings(data.items || []))
      .finally(() => setLoading(false));
  }, [categorySlug, country, type]);

  function updateParam(key, val) {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next);
  }

  return (
    <div
      data-testid="category-browse"
      className="min-h-screen"
      style={{ background: "var(--wofy-bg)" }}
    >
      <Helmet>
        <title>{`${category?.name || categorySlug} · SocialPulse Local`}</title>
      </Helmet>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        {/* Breadcrumb */}
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] mb-6 opacity-60">
          <Link to="/local" className="hover:opacity-100">SocialPulse Local</Link>
          <span className="mx-2">/</span>
          <span>{category?.name || categorySlug}</span>
        </div>

        {/* Header */}
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <h1
            data-testid="browse-heading"
            className="font-display text-3xl md:text-5xl leading-tight tracking-tight"
            style={{ color: "var(--wofy-text)" }}
          >
            {category?.icon && <span className="mr-2">{category.icon}</span>}
            {category?.name || categorySlug}
          </h1>
          <div className="flex items-center gap-2 text-sm opacity-70">
            <MapPin className="w-4 h-4" />
            <select
              data-testid="browse-country-select"
              value={country}
              onChange={(e) => updateParam("country", e.target.value)}
              className="bg-transparent outline-none"
            >
              {countries.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter chips */}
        <div
          data-testid="browse-filters"
          className="mt-6 flex flex-wrap gap-2 items-center border-b pb-6"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <Filter className="w-4 h-4 opacity-60 mr-1" />
          <button
            onClick={() => updateParam("type", "")}
            className="rounded-full px-3 py-1 text-[13px] border"
            style={{
              borderColor: !type ? "#0E7C55" : "var(--wofy-border)",
              background: !type ? "rgba(14,124,85,0.08)" : "white",
              color: !type ? "#0E7C55" : "var(--wofy-text)",
            }}
          >
            All types
          </button>
          {LISTING_TYPES.slice(0, 8).map((t) => (
            <button
              key={t.key}
              data-testid={`browse-filter-${t.key}`}
              onClick={() => updateParam("type", type === t.key ? "" : t.key)}
              className="rounded-full px-3 py-1 text-[13px] border"
              style={{
                borderColor: type === t.key ? "#0E7C55" : "var(--wofy-border)",
                background: type === t.key ? "rgba(14,124,85,0.08)" : "white",
                color: type === t.key ? "#0E7C55" : "var(--wofy-text)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="mt-10 text-sm opacity-60">Loading listings…</div>
        ) : listings.length === 0 ? (
          <div
            data-testid="browse-empty"
            className="mt-10 rounded-md border border-dashed p-12 text-center"
            style={{ borderColor: "var(--wofy-border)" }}
          >
            <p className="text-[15px] mb-4 opacity-70">
              No {category?.name?.toLowerCase() || categorySlug} listings yet in {currentCountry?.name || country}.
            </p>
            <Link to="/local" className="wofy-btn-ghost inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Post the first one
            </Link>
          </div>
        ) : (
          <div
            data-testid="browse-results"
            className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {listings.map((l) => (
              <Link
                key={l.id}
                to={`/local/listings/${l.id}`}
                data-testid={`browse-listing-${l.id}`}
                className="rounded-md border p-4 hover:-translate-y-0.5 transition"
                style={{ borderColor: "var(--wofy-border)", background: "white" }}
              >
                <div className="text-[11px] font-mono uppercase tracking-[0.15em] opacity-60 mb-2 flex items-center justify-between">
                  <span>{typeByKey(l.listing_type).label}</span>
                  <span>{timeAgo(l.created_at)}</span>
                </div>
                <div className="font-display text-lg leading-tight mb-1 line-clamp-2"
                     style={{ color: "var(--wofy-text)" }}>
                  {l.title}
                </div>
                <div className="text-sm opacity-70 line-clamp-2 mb-3">
                  {l.description}
                </div>
                <div className="text-sm flex items-center justify-between">
                  <span className="opacity-60">
                    <MapPin className="w-3.5 h-3.5 inline mr-1" />
                    {l.location?.city || l.location?.country_code}
                  </span>
                  {l.pricing?.[0]?.model === "free" ? (
                    <span className="font-medium" style={{ color: "#0E7C55" }}>Free</span>
                  ) : l.pricing?.[0]?.amount_minor ? (
                    <span className="font-medium">
                      {formatMoney(l.pricing[0].amount_minor, l.pricing[0].currency)}
                    </span>
                  ) : null}
                </div>
              </Link>
            ))}
          </div>
        )}

        <Link
          to="/local"
          className="mt-14 inline-flex items-center gap-1.5 text-sm opacity-70 hover:opacity-100 underline"
        >
          <ArrowLeft className="w-4 h-4" /> Back to SocialPulse Local
        </Link>
      </div>
    </div>
  );
}
